// ==========================================
// Workflow palette (1d / 3d)
// ==========================================
// Opens over the last 40% of the chat log — not a modal, so the conversation stays
// visible behind it. Tap a tile to run with defaults, long-press for parameters,
// and "Define new" records the next exchange as a reusable workflow.
// ==========================================

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';

import { Workflow } from '../../models/buddy.types';
import { ACCENT_HEX, MIC_HOLD_MS } from '../../models/buddy.constants';

@Component({
  selector: 'app-buddy-workflow-palette',
  standalone: false,
  templateUrl: './buddy-workflow-palette.component.html',
  styleUrls: ['./buddy-workflow-palette.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuddyWorkflowPaletteComponent implements OnDestroy {
  @Input() workflows: readonly Workflow[] = [];
  /** Echoes whatever "/" filter the input row is carrying. */
  @Input() query = '';

  @Output() runRequested = new EventEmitter<string>();
  @Output() paramsRequested = new EventEmitter<string>();
  @Output() defineRequested = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private didHold = false;

  accentFor(workflow: Workflow): string {
    return ACCENT_HEX[workflow.accent];
  }

  onPressStart(workflow: Workflow): void {
    this.didHold = false;
    this.clearHold();
    this.holdTimer = setTimeout(() => {
      this.didHold = true;
      this.holdTimer = null;
      this.paramsRequested.emit(workflow.id);
    }, MIC_HOLD_MS);
  }

  onPressEnd(workflow: Workflow): void {
    this.clearHold();
    if (!this.didHold) {
      this.runRequested.emit(workflow.id);
    }
    this.didHold = false;
  }

  onPressCancel(): void {
    this.clearHold();
    this.didHold = false;
  }

  trackWorkflow(_: number, workflow: Workflow): string {
    return workflow.id;
  }

  private clearHold(): void {
    if (this.holdTimer !== null) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearHold();
  }
}
